import moment from 'moment'
import jwtDecode from 'jwt-decode'
import { store } from '../config/redux'
import Util from './Util'

export const orderTypes = {
    DELIVERY: 'delivery',
    PICKUP: 'pickup',
}

export const itemOperations = {
    ADD: 'add',
    REMOVE: 'remove',
}

export const countryCode = {
    US: 'US',
    CA: 'CA',
}

/* Check if:
1. Token exists or not
2. It is a guest token or a signed in token
3. If it is a signed in token, return the customer ID
*/

export const isEmpty = value => {
    if (value == null) return true

    if (typeof value === 'boolean' || typeof value === 'number') return false

    if (typeof value === 'string') return value.trim().length === 0

    if (Array.isArray(value)) return value.length === 0

    if (typeof value === 'object') {
        if (value instanceof Set || value instanceof Map) {
            return value.size === 0
        }
        return Object.keys(value).length === 0
    }

    return false
}

const isObject = obj => obj && typeof obj === 'object'
export const isEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true

    if (!isObject(obj1) || !isObject(obj2)) return false

    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)

    if (keys1.length !== keys2.length) return false

    for (const key of keys1) {
        if (!keys2.includes(key) || !isEqual(obj1[key], obj2[key])) return false
    }

    return true
}
export const getJWTDetails = (jwt = null) => {
    const { appSettings } = store.getState()
    const token = jwt || appSettings.get('jwt')
    const refreshToken = appSettings.get('refreshJWT')
    if (token) {
        const { scope, sub, customer_id, refresh_token } = jwtDecode(token)
        return {
            tokenExists: true,
            isSignedIn:
                scope &&
                scope.length > 0 &&
                (scope.includes('signedIn') ||
                    scope.includes('read_profile') ||
                    scope.includes('update_profile')),
            customer_id: !isEmpty(customer_id)
                ? customer_id
                : sub && sub.digital_id
                    ? sub.digital_id
                    : null,
            token,
            refreshToken: !isEmpty(refreshToken) ? refreshToken : refresh_token || null,
        }
    }

    return {
        tokenExists: false,
        isSignedIn: false,
        customer_id: '',
        token: null,
        refreshToken: null,
    }
}

export const isDeliveryMode = () => {
    const { location, appSettings, basket } = store.getState()
    const { basketItems } = basket?.toJS()
    const isCateringMode = appSettings.get('isCateringMode') || Util.checkForPreOrderItems(basketItems)

    // In catering mode, always return false (pickup mode)
    if (isCateringMode) {
        return false
    }

    const selectedAddress = location.get('selectedAddress')
    if (selectedAddress && selectedAddress.get('type') === orderTypes.PICKUP) {
        return false
    }
    return true
}

export const shouldShowPickupOnlyMsg = item => isDeliveryMode() && item?.platter_item_flag === '1'

export const isConversionRequired = () => {
    const { tokenExists, isSignedIn, customer_id } = getJWTDetails()
    return {
        isConversion: tokenExists && !isSignedIn,
        customer_id,
    }
}

export const debounce = () => {
    let instanceID = ''
    return function (callback, dTime = 300) {
        if (instanceID) {
            clearTimeout(instanceID)
        }
        instanceID = setTimeout(callback, dTime)
    }
}

export const isAppRefreshRequired = () => {
    const { products, configuration } = store.getState()
    const lastFetched = (products && products.lastHomePageFetched) || null

    const productsCacheTime = configuration.toJS().products_categories_cache_time
    const currentTime = Date.now()

    if (lastFetched && (currentTime - lastFetched) / (1000 * 60) > productsCacheTime / 60)
        return true
    return false
}

export const isProductsRefreshRequired = () => {
    const { location } = store.getState()
    const { selectedAddress } = location.toJS()

    // If its global assortment, do not refresh products
    if (!selectedAddress) return false
    return isHomePageDataStale()
}

export const isHomePageDataStale = () => {
    const { products, configuration } = store.getState()
    const lastFetched = (products && products.lastHomePageFetched) || null

    const productsCacheTime = configuration.toJS().products_categories_cache_time
    const currentTime = Date.now()

    if (!lastFetched || (currentTime - lastFetched) / (1000 * 60) > productsCacheTime / 60)
        return true
    return false
}

export const isDeliveryOrder = order => order && order.order_type === orderTypes.DELIVERY

export const getDeliveryNotesMaxLength = () => {
    const { configuration } = store.getState()
    const delivery_notes_length = configuration.toJS().delivery_notes_length || '210'

    return delivery_notes_length
}

export const cleanParamsURL = item =>
    item.toLowerCase().replace(/[/\s]/g, '-').replace(/%20/g, '-').replace(/%/g, '%25')

export const isRewardsEnabled = response => {
    if (response && response.rewards_enabled !== undefined) {
        return response.rewards_enabled === true
    }
    return false
}

export const range = (start, end, step) =>
    Array.from(Array.from(Array(Math.ceil((end - start) / step)).keys()), x => start + x * step)

export const getSubscriptionStatus = (arr, id) => {
    const subscriptionFromConfig = arr.find(e => e.sub_id === id)
    if (subscriptionFromConfig) {
        const { status } = subscriptionFromConfig
        return status
    }
    return null
}

export const capitalizeFirstLetter = string => string.charAt(0).toUpperCase() + string.slice(1)
export const camelCase = string => string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();

export const getCurrentCountry = (location, user) => {
    const selectedAddress = location ? location.toJS().selectedAddress : null
    const countryForGlobalAssortment = location ? location.toJS().countryForGlobalAssortment : null
    const tempAddress = location ? location.toJS().selectedTempAddress : null
    const userCountryFromIP = countryCode.US
    const userProfile = user ? user.toJS().userProfile : null

    if (tempAddress && tempAddress !== selectedAddress) {
        return tempAddress.country === countryCode.CA ? countryCode.CA : countryCode.US
    }
    if (selectedAddress && selectedAddress.country) {
        return selectedAddress.country === countryCode.CA ? countryCode.CA : countryCode.US
    }

    if (userProfile && userProfile.country) {
        return userProfile.country === countryCode.CA ? countryCode.CA : countryCode.US
    }

    if (!selectedAddress && countryForGlobalAssortment) {
        return countryForGlobalAssortment
    }

    return userCountryFromIP
}

export const formatUSPhoneNumber = phoneNumber => {
    const cleaned = `${phoneNumber}`.replace(/\D/g, '')

    if (cleaned.length !== 10) {
        return null
    }

    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
    if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`
    }

    return null
}

export const isValidUSPhoneNumber = phoneNumber => {
    const usPhoneNumberPattern = /^\(?([2-9][0-8][0-9])\)?[-.\s]?([2-9][0-9]{2})[-.\s]?([0-9]{4})$/
    return usPhoneNumberPattern.test(phoneNumber)
}

// calculating distance of two given lat long co-ordinates by using Haversine Formula
export const haversineDistance = (coords1, coords2) => {
    const toRadians = degrees => degrees * (Math.PI / 180)

    const [lat1, lon1] = coords1
    const [lat2, lon2] = coords2

    const R = 6371.0 // Radius of the Earth in miles or kilometers
    const dLat = toRadians(lat2 - lat1)
    const dLon = toRadians(lon2 - lon1)
    const lat1Rad = toRadians(lat1)
    const lat2Rad = toRadians(lat2)

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1Rad) * Math.cos(lat2Rad)

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return Math.ceil(R * c) // Distance in kilometers
}

export const isReferralEnable = () => {
    const { user, configuration, orders } = store.getState()
    const { isSignedIn } = getJWTDetails()
    const date = user && user.getIn(['userProfile', 'enroll_date'])
    const enrollDate = moment(date)
    const currentDate = moment(new Date())
    const activeOrderCount = user && user.getIn(['userProfile', 'active_orders_count'])
    const pastDeliveryOrders =
        user && user.getIn(['userProfile', 'past_orders_count_by_type', 'delivery'])
    const pastPickupOrders =
        user && user.getIn(['userProfile', 'past_orders_count_by_type', 'pickup'])

    const referAFriendEnabled = configuration && configuration.get('refer_a_friend_enabled')
    const userSignupDateRange = configuration && configuration.get('user_signup_date_range')
    const isAlreadyRedeemed = orders && orders.get('orderPlaced')
    const difference = currentDate.diff(enrollDate, 'days')
    if (referAFriendEnabled) {
        if (
            userSignupDateRange > 0 &&
            difference <= userSignupDateRange &&
            isSignedIn &&
            activeOrderCount === 0 &&
            pastDeliveryOrders + pastPickupOrders === 0 &&
            !isAlreadyRedeemed
        ) {
            return true
        }
    }
    return false
}

export const getFullYear = () => new Date().getFullYear()
export const getCurrentMonthName = () => new Date().toLocaleString('default', { month: 'long' })

/**
 *
 * @param {*} country
 * @returns
 */
export const getPickupStoreKey = country => `PICKUP_STORE_${country}`
/**
 *
 * @param {*} country
 * @returns
 */
export const getConfigKey = country => `CONFIGURATION_${country}`
/**
 *
 * @returns
 */
export const getFAQsKey = () => `FAQS`
